import { ParlorDetailProps } from "@/types/parlor";

/**
 * 가격을 새로 공개하지 않는 장례식장. public/robots.txt 의 `/parlor/{id}/` 차단 목록과 같다.
 * 차단 사유에 장례식장 측 요청, 비공개 협약이 섞여 있어(wiki/features/parlor-detail-page.md)
 * 가격 비공개 이력이 있는 곳(excludeParlors.ts 주석 목록 16곳)이 전부 여기 들어 있다.
 * robots 에 차단을 추가하면 여기도 추가해야 한다.
 */
const PRICE_HIDDEN_PARLOR_IDS = new Set([
  6, 7, 21, 42, 43, 75, 76, 97, 98, 111, 123, 124, 156, 162, 164, 174, 191, 194, 195, 199, 202, 209,
  211, 229, 247, 262, 278, 283, 285, 325, 326, 360, 402, 463, 505, 519, 538, 550, 585, 595, 611,
  641, 666, 680, 690, 700, 708, 766, 771, 792, 830, 833, 863, 908, 915, 927, 953, 996, 1051, 1173,
  1221, 1223,
]);

/**
 * 장례식장 상세 가격 공개 여부.
 *
 * 2026-10-07 짝수 id 만 공개하는 SEO 대조 실험으로 시작했다가, 2026-10-08 유입 회복을 우선해
 * 전체 공개로 바꿨다(실험 종료). 빈소 사용료, 시설 이용료 표와 데이터 기반 FAQ(화면 + FAQPage JSON-LD).
 *
 * robots 차단(비공개 요청 포함)과 is_excluded(폐업, 휴업 등) 장례식장은 공개하지 않는다.
 */
export const isPriceDisclosureParlor = (parlor: Pick<ParlorDetailProps, "id" | "is_excluded">) =>
  !isHiddenParlor(parlor);

/**
 * robots 차단(비공개 요청, 삭제) 이거나 폐업, 휴업 장례식장. 가격 공개와 상황별 바로가기를 둘 다 막는다.
 * parlor 도메인 directions 가 없는 www 장례식장 54곳도 전부 여기 걸려, 바로가기 링크가 404 가 되지 않는다.
 */
export const isHiddenParlor = (parlor: Pick<ParlorDetailProps, "id" | "is_excluded">) =>
  Boolean(parlor.is_excluded) || PRICE_HIDDEN_PARLOR_IDS.has(Number(parlor.id));

export const PRICE_SOURCE_NOTICE =
  "e하늘 장사정보시스템 공개 가격 기준이며, 실제 요금은 장례식장에 확인해 주세요.";

const BINSO_NAME_WORDS = /장례식장|빈소|시설|임대료|사용료/g;

const SERVICE_ROWS = [
  { key: "안치료", label: "안치료 (1일 기준)" },
  { key: "수시비", label: "수시비" },
  { key: "입관실", label: "입관실" },
  { key: "쓰레기 수거료", label: "쓰레기 수거료" },
];

const won = (n: number) => `${n.toLocaleString()}원`;

export function getParlorPriceDisclosure(parlor: ParlorDetailProps) {
  const name = parlor.companyname;

  const binsos = (parlor.binsos ?? [])
    .filter((b) => b.charge > 0)
    .sort((a, b) => a.charge - b.charge)
    .map((b, i) => {
      const cleaned = b.item.replace(BINSO_NAME_WORDS, " ").replace(/\s+/g, " ").trim();
      return {
        name: !cleaned || cleaned === "1일" ? `빈소 ${i + 1}` : cleaned,
        pyeong: b.pyeong ? `${Number(b.pyeong.toFixed(1))}평` : "",
        charge: won(b.charge),
      };
    });

  const charge = (key: string) => {
    const c = parlor.services?.[key]?.charge;
    return c && c > 0 ? c : null;
  };
  const services = SERVICE_ROWS.filter((s) => charge(s.key)).map((s) => ({
    label: s.label,
    charge: won(charge(s.key) as number),
  }));

  const faqs: { question: string; answer: string }[] = [];

  if (binsos.length > 0) {
    const min = binsos[0].charge;
    const max = binsos[binsos.length - 1].charge;
    faqs.push({
      question: `${name} 빈소는 몇 개이고 사용료는 얼마인가요?`,
      answer: `${name}에는 빈소가 총 ${binsos.length}실 있으며, 1일 사용료는 ${
        min === max ? `${min}입니다.` : `${min}부터 ${max}까지입니다.`
      }`,
    });
  }

  // 무빈소를 모르는 사람이 많다. 화면 FAQ 와 FAQPage JSON-LD 에 같이 들어간다
  const mubinsoCost = parlor.expected_charge_sum?.["무빈소"];
  if (mubinsoCost) {
    faqs.push({
      question: `${name}에서 무빈소 장례도 가능한가요?`,
      answer: `무빈소는 빈소를 차리지 않고 조문객 없이 가족끼리 진행하는 장례입니다. ${name} 기준 무빈소 예상 비용은 약 ${Math.round(
        mubinsoCost / 10000,
      ).toLocaleString()}만원입니다. 진행 가능 여부는 안치실 여유와 장례식장 방침에 따라 달라 고이장례연구소 24시간 상담(1666-9784)에서 확인해 드립니다.`,
    });
  }

  const anchi = charge("안치료");
  if (anchi) {
    faqs.push({
      question: `${name} 안치료는 얼마인가요?`,
      answer: `안치료는 1일 ${won(anchi)}입니다. 3일장으로 3일 안치하면 약 ${won(
        anchi * 3,
      )}입니다.`,
    });
  }

  const ipgwan = charge("입관실");
  const susi = charge("수시비");
  if (ipgwan || susi) {
    faqs.push({
      question: `${name} 입관실 사용료와 수시비는 얼마인가요?`,
      answer: [ipgwan ? `입관실 사용료는 ${won(ipgwan)}` : "", susi ? `수시비는 ${won(susi)}` : ""]
        .filter(Boolean)
        .join(", ")
        .concat("입니다."),
    });
  }

  const parkcnt = Number(parlor.amenities?.parkcnt) || 0;
  if (parkcnt > 0 || parlor.amenities?.has_parkinglot) {
    faqs.push({
      question: `${name}에 주차할 수 있나요?`,
      answer:
        parkcnt > 0
          ? `네, 약 ${parkcnt.toLocaleString()}대 규모의 주차장이 있습니다.`
          : "네, 주차장이 있습니다. 주차 규모는 장례식장에 확인해 주세요.",
    });
  }

  if (parlor.telephone) {
    faqs.push({
      question: `${name} 전화번호는 어떻게 되나요?`,
      answer: `${name} 대표 전화번호는 ${parlor.telephone}입니다.`,
    });
  }

  return { binsos, services, faqs };
}

export const toFaqPageJsonLd = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: { "@type": "Answer", text: faq.answer },
  })),
});
